import type { ReactNode } from 'react';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useQuery } from '@tanstack/react-query';
import {
  AssetSchema,
  ClientSchema,
  EmployeeSchema,
  ProjectSchema,
  ReferenceMasterSchema,
  type SupplierInvoiceDetailLineDto,
} from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { ApiError, getData, getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, PageHeader, Section } from '../../shared/ui/page';

/**
 * Pieces every finance record page shares: the facts table, the refusal of a record that is
 * missing or not visible, a link to a related record, and a document's lines with their accounts,
 * dimensions and retained VAT (SDD §8.9).
 */

export function Facts({ label, facts }: { label: string; facts: readonly [string, ReactNode][] }) {
  return (
    <Table size="small" aria-label={label}>
      <TableBody>
        {facts.map(([name, value]) => (
          <TableRow key={name}>
            <TableCell
              component="th"
              scope="row"
              sx={{ width: { xs: 140, sm: 220 }, color: 'text.secondary' }}
            >
              {name}
            </TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** A record that failed to load: "no such record" for a 404, the API's message otherwise. */
export function RecordFailure({
  title,
  noun,
  error,
  back,
}: {
  title: string;
  noun: string;
  error: Error;
  back: ReactNode;
}) {
  const missing = error instanceof ApiError && error.status === 404;
  return (
    <Stack spacing={3}>
      <PageHeader title={title} actions={back} />
      {missing ? (
        <Section>
          <EmptyState
            title={`No such ${noun}`}
            detail="It does not exist, or you may not see it."
          />
        </Section>
      ) : (
        <ErrorState message={error.message} />
      )}
    </Stack>
  );
}

/** A link to a related record's own page, by the text that names it. */
export function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={to} sx={{ fontWeight: 600 }}>
      {children}
    </Link>
  );
}

/**
 * The journal a document posted (SDD §8.9: source → journal), linked to its page for a session
 * that may read journals; named but not linked otherwise.
 */
export function JournalRef({
  journal,
}: {
  journal: { id: string; summary: string | null } | null;
}) {
  const session = useSession();
  if (journal === null) return <>Not posted</>;
  const text = journal.summary ?? 'Posted';
  return hasCapability(session.data, 'ledger.journal:view') ? (
    <RecordLink to={`/journals/${journal.id}`}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}

/** Names for the dimension references, read from their masters where the session may. */
export function useDimensionNames() {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const costCentres = useQuery({
    queryKey: ['masters-all', 'cost-centers'],
    enabled: can('organization.cost_center:view'),
    queryFn: () =>
      getPage('/api/v1/organization/cost-centers?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const locations = useQuery({
    queryKey: ['masters-all', 'locations'],
    enabled: can('organization.location:view'),
    queryFn: () =>
      getPage('/api/v1/organization/locations?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'all'],
    enabled: can('hr.employee:view'),
    queryFn: () => getPage('/api/v1/hr/employees?pageSize=200&sort=name', EmployeeSchema),
  });
  return {
    costCentre: (id: string | null) => nameOf(costCentres, id),
    location: (id: string | null) => nameOf(locations, id),
    employee: (id: string | null) => nameOf(employees, id),
  };
}

type DimensionKind = 'client' | 'project' | 'asset';

const DIMENSIONS: Readonly<
  Record<DimensionKind, { label: string; api: string; page: string; capability: string }>
> = {
  client: {
    label: 'Client',
    api: '/api/v1/crm/clients',
    page: '/crm/clients',
    capability: 'crm.client:view',
  },
  project: {
    label: 'Project',
    api: '/api/v1/projects',
    page: '/projects',
    capability: 'projects.project:view',
  },
  asset: {
    label: 'Asset',
    api: '/api/v1/assets/assets',
    page: '/assets',
    capability: 'assets.asset:view',
  },
};

/**
 * A Client, Project or Asset dimension on a line (FRD-FIN-019), named from its own module and
 * linked to its page where the session may read it; otherwise the reference alone is shown.
 */
export function DimensionRef({ kind, id }: { kind: DimensionKind; id: string | null }) {
  const session = useSession();
  const dimension = DIMENSIONS[kind];
  const visible = id !== null && hasCapability(session.data, dimension.capability);
  const record = useQuery({
    queryKey: ['dimension-ref', kind, id],
    enabled: visible,
    retry: false,
    queryFn: async () => {
      const path = `${dimension.api}/${id ?? ''}`;
      if (kind === 'client') {
        const c = await getData(path, ClientSchema);
        return `${c.code} — ${c.companyName}`;
      }
      const r = await getData(path, kind === 'project' ? ProjectSchema : AssetSchema);
      return `${r.code} — ${r.name}`;
    },
  });
  if (id === null) return <>—</>;
  const text = `${dimension.label}: ${record.data ?? `#${id}`}`;
  return visible && record.isSuccess ? (
    <RecordLink to={`${dimension.page}/${encodeURIComponent(id)}`}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}

/**
 * A referenced record by code and name. While its master is still loading it says so; only a
 * record the session may not read (or cannot find) is "Not visible".
 */
function nameOf(
  master: {
    isLoading: boolean;
    data?: { items: readonly { id: string; code: string; name: string }[] } | undefined;
  },
  id: string | null,
): string {
  if (id === null) return '—';
  if (master.isLoading) return '…';
  const found = master.data?.items.find((item) => item.id === id);
  return found === undefined ? 'Not visible' : `${found.code} — ${found.name}`;
}

/** A document line; a credit note line also names the invoice line it credits. */
type DocumentLine = SupplierInvoiceDetailLineDto & {
  creditedInvoiceLine?: { id: string; lineNo: number } | null;
};

/**
 * A document's lines: account, dimensions, net and the VAT retained at capture. On a credit note
 * each line also shows the invoice line it credits and the original VAT record its VAT adjusts
 * (FRD-VAT-024, VAT-037).
 */
export function DocumentLines({
  label,
  lines,
  creditNote = false,
}: {
  label: string;
  lines: readonly DocumentLine[];
  creditNote?: boolean | undefined;
}) {
  const names = useDimensionNames();
  const other = (line: SupplierInvoiceDetailLineDto) =>
    (
      [
        ['client', line.clientId],
        ['project', line.projectId],
        ['asset', line.assetId],
      ] as const
    ).filter((entry): entry is [DimensionKind, string] => entry[1] !== null);
  return (
    <TableContainer>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Description</TableCell>
            {creditNote && <TableCell>Credits invoice line</TableCell>}
            <TableCell>Account</TableCell>
            <TableCell>Cost centre</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>Location</TableCell>
            {!creditNote && <TableCell>Campaign</TableCell>}
            <TableCell align="right">Net</TableCell>
            <TableCell>VAT treatment</TableCell>
            <TableCell align="right">Rate %</TableCell>
            <TableCell align="right">VAT</TableCell>
            <TableCell align="right">Recoverable</TableCell>
            <TableCell align="right">Non-recoverable</TableCell>
            {creditNote && <TableCell>Adjusts VAT of</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell>{line.lineNo}</TableCell>
              <TableCell>
                {line.description}
                {other(line).map(([kind, dimensionId]) => (
                  <div key={kind}>
                    <small>
                      <DimensionRef kind={kind} id={dimensionId} />
                    </small>
                  </div>
                ))}
              </TableCell>
              {creditNote && (
                <TableCell>{creditedText(line.creditedInvoiceLine ?? null)}</TableCell>
              )}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {line.accountCode === null
                  ? '—'
                  : `${line.accountCode} — ${line.accountName ?? ''}`}
              </TableCell>
              <TableCell>{names.costCentre(line.costCenterId)}</TableCell>
              <TableCell>{names.employee(line.employeeId)}</TableCell>
              <TableCell>{names.location(line.locationId)}</TableCell>
              {!creditNote && (
                <TableCell>
                  <LineCampaign id={line.campaignId} summary={line.campaignSummary} />
                </TableCell>
              )}
              <TableCell align="right">{line.netAmount}</TableCell>
              <TableCell>{line.vat?.treatment.replace(/_/g, ' ') ?? '—'}</TableCell>
              <TableCell align="right">{line.vat?.rateApplied ?? '—'}</TableCell>
              <TableCell align="right">{line.vat?.vatAmount ?? '—'}</TableCell>
              <TableCell align="right">{line.vat?.recoverableAmount ?? '—'}</TableCell>
              <TableCell align="right">{line.vat?.nonRecoverableAmount ?? '—'}</TableCell>
              {creditNote && (
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {adjustmentText(line.vat, line.creditedInvoiceLine ?? null)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** The original a credit note line's VAT adjusts: the invoice (reference) and, where named, its line. */
function adjustmentText(
  vat: SupplierInvoiceDetailLineDto['vat'],
  credited: { lineNo: number } | null,
): string {
  if (vat === null || vat.adjustmentReference === null) return '—';
  return vat.adjustedDetailId === null || credited === null
    ? vat.adjustmentReference
    : `${vat.adjustmentReference}, line ${String(credited.lineNo)}`;
}

function creditedText(credited: { lineNo: number } | null): string {
  return credited === null ? 'None (general credit)' : `Line ${String(credited.lineNo)}`;
}

/**
 * The campaign an invoice line names (CLR-18), linked to the campaign's page for a session that may
 * view campaigns; the invoice itself stays governed by its own permission.
 */
function LineCampaign({ id, summary }: { id: string | null; summary: string | null }) {
  const session = useSession();
  if (id === null) return <>—</>;
  const text = summary ?? `Campaign #${id}`;
  return hasCapability(session.data, 'marketing.campaign:view') ? (
    <RecordLink to={`/marketing/campaigns/${id}`}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}
